/**
 * Unsaved-changes draft model + predicate (E10-F02-T02).
 *
 * Parity with the legacy `dashboard.js → hasUnsavedChanges()`. The live app
 * tracked four independent sources of "dirty" state on the open project detail
 * card and refused to navigate away (or close) silently when any were set:
 *
 *   1. `unsavedUpdates`  — a bag of deferred field edits (client_name, role_name,
 *      contact{}, manual_context, client_id, …) staged by the inline editors but
 *      not yet persisted via `update_project`. Any non-empty entry is dirty.
 *   2. visible inline edit-fields — an edit-block toggled open (e.g. the client
 *      or role name input shown via `toggleEditBlock`) counts as dirty even
 *      before the user types, because closing it loses the in-progress edit.
 *   3. a changed `manual_context` text — the free-text context textarea whose
 *      current value differs from the persisted `projects.data.manual_context`.
 *   4. `pendingFiles` — files picked for upload but not yet sent in the multipart
 *      `saveProjectChanges` FormData.
 *
 * This module is the pure, framework-free core: a serialisable {@link UnsavedDraft}
 * shape plus {@link hasUnsavedChanges}. The hook ({@link useUnsavedGuard}) and the
 * dialog ({@link UnsavedDialog}) build on top of it. Keeping the predicate pure
 * makes the "dirty vs clean" decision trivially testable and reusable by the
 * future project-detail feature without dragging React in.
 */

/** A single staged file awaiting upload (the picked `File` is opaque here). */
export interface PendingFile {
  /** Display name (used for parity logging / the dialog copy if ever shown). */
  readonly name: string;
  /** The picked blob; only its presence matters to the predicate. */
  readonly file?: unknown;
}

/**
 * The serialisable snapshot of everything that makes a project-detail card
 * "dirty". Every field is optional so a caller can supply only the sources it
 * actually tracks; an entirely empty/omitted draft is clean.
 */
export interface UnsavedDraft {
  /**
   * Deferred field edits staged by the inline editors (client_name, role_name,
   * contact, manual_context, client_id, …). Any own enumerable key with a
   * non-`undefined` value makes the draft dirty. An explicit `undefined` is a
   * "cleared" marker and does NOT count (parity: `delete unsavedUpdates.k`).
   */
  readonly unsavedUpdates?: Readonly<Record<string, unknown>>;
  /**
   * Identifiers of inline edit-blocks currently toggled open (e.g.
   * `['client', 'role']`). A visible edit-field is dirty on its own — closing it
   * discards the in-progress edit even if `unsavedUpdates` is still empty.
   */
  readonly openEditFields?: readonly string[];
  /**
   * The manual-context textarea's CURRENT text. Compared against
   * {@link savedManualContext}; a difference (after both are normalised) is dirty.
   */
  readonly manualContextText?: string;
  /**
   * The manual-context text as last PERSISTED (`projects.data.manual_context.text`).
   * Used only as the comparison baseline for {@link manualContextText}.
   */
  readonly savedManualContext?: string;
  /** Files picked for upload but not yet sent. A non-empty list is dirty. */
  readonly pendingFiles?: readonly PendingFile[];
}

/** Normalise context text for comparison: undefined/null → '', trimmed. */
function normaliseContext(value: string | undefined): string {
  return (value ?? '').trim();
}

/**
 * True when the draft has at least one unsaved change.
 *
 * The four sources are OR-ed exactly as the legacy `hasUnsavedChanges` did:
 *
 *   - `unsavedUpdates` has any own key whose value is not `undefined`
 *     (an `undefined` value is treated as a cleared/absent entry);
 *   - any inline edit-field is open;
 *   - the manual-context text differs from the saved baseline (trimmed compare);
 *   - there is at least one pending file.
 *
 * A missing/empty draft (`{}` or `undefined`) is clean → `false`, which is what
 * lets the guard fall through to a direct navigation.
 */
export function hasUnsavedChanges(draft: UnsavedDraft | undefined | null): boolean {
  if (!draft) return false;

  const { unsavedUpdates, openEditFields, manualContextText, savedManualContext, pendingFiles } =
    draft;

  // 1. staged field edits — any non-undefined own value.
  if (unsavedUpdates) {
    for (const key of Object.keys(unsavedUpdates)) {
      if (unsavedUpdates[key] !== undefined) return true;
    }
  }

  // 2. a visible inline edit-field.
  if (openEditFields && openEditFields.length > 0) return true;

  // 3. changed manual-context text (only when the field is actually being
  //    tracked — i.e. `manualContextText` was supplied).
  if (manualContextText !== undefined) {
    if (normaliseContext(manualContextText) !== normaliseContext(savedManualContext)) {
      return true;
    }
  }

  // 4. pending file uploads.
  if (pendingFiles && pendingFiles.length > 0) return true;

  return false;
}
